import { FormattedMessage } from "react-intl"
import {
  Box,
  Button,
  Chip,
  DownIcon,
  Link,
  Stack,
  Theme,
  Typography,
  UpIcon,
  useMediaQuery,
  useTheme
} from "@carrotfertility/carotene-core"
import React, { useState } from "react"
import { TypeCollectiveItem, TypeContentBlockSkeleton } from "#/types/contentful"
import { ContentfulContentRenderer } from "#/services/contentful/content-renderer"
import { BaseTile } from "./BaseTile"
import { DynamicCtaButton } from "./DynamicCtaButton"
import { Asset, Entry } from "contentful"
import { DbgPaths } from "#/utils/Paths"
import { Link as ReactRouterLink } from "react-router-dom"
import { useTrackCollectiveItemAction } from "./hooks/useTrackCollectiveItemAction"
import { MemberActions } from "#/services/memberActionTracking/memberActions"

type CollectiveItemEntry = TypeCollectiveItem<"WITHOUT_UNRESOLVABLE_LINKS">

export default function CollectiveItemTile({
  entry,
  isPro
}: {
  entry: CollectiveItemEntry
  isPro: boolean
}): JSX.Element {
  const [showAdditionalInformation, setShowAdditionalInformation] = useState(false)
  const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const fields = entry.fields
  const trackAction = useTrackCollectiveItemAction(fields.internalName, fields.headline, fields.offerCopy)

  const handleLearnMoreClick = () => {
    if (!showAdditionalInformation) {
      trackAction(MemberActions.CARROT_COLLECTIVE_CLICKED_LEARN_MORE)
    }
    setShowAdditionalInformation(!showAdditionalInformation)
  }

  const learnMoreToggle = <LearnMoreToggle isExpanded={showAdditionalInformation} onClick={handleLearnMoreClick} />

  return (
    <BaseTile sx={{ padding: 0, display: "flex", flexDirection: "column" }} data-testid={`tile-${entry.sys.id}`}>
      <BrandImage image={fields.brandImage} altText={fields.brandImageAltCopy} />
      <TileContentContainer>
        <UpperContentGroup>
          <LogoAndOfferHeader logo={fields.logo} logoAlt={fields.logoAltCopy} offerCopy={fields.offerCopy} />
          <Headline text={fields.headline} />
          <ContentBlock content={fields.body} />
          {showAdditionalInformation && (
            <>
              <ContentBlock content={fields.expandedInformation} />
              {isPro && <LearnAboutCoverageLink trackClick={trackAction} />}
            </>
          )}
          {!isMobileView && learnMoreToggle}
        </UpperContentGroup>
        <LowerActionGroup>
          <CtaButton url={fields.ctaUrl} text={fields.ctaCopy} method={fields.ctaMethod} trackClick={trackAction} />
          {isMobileView && learnMoreToggle}
        </LowerActionGroup>
      </TileContentContainer>
    </BaseTile>
  )
}

function BrandImage({ image, altText }: { image?: Asset; altText?: string }): JSX.Element | null {
  if (!image) return null

  return (
    <Box
      sx={{
        aspectRatio: "16 / 9",
        overflow: "hidden"
      }}
    >
      <Box
        component="img"
        src={`https:${image.fields.file.url}`}
        aria-label={altText || ""}
        alt={altText || ""}
        sx={{
          inlineSize: "100%",
          blockSize: "100%",
          objectFit: "cover"
        }}
      />
    </Box>
  )
}

function LogoAndOfferHeader({
  logo,
  logoAlt,
  offerCopy
}: {
  logo?: Asset
  logoAlt?: string
  offerCopy?: string
}): JSX.Element | null {
  const theme = useTheme()

  if (!logo && !offerCopy) return null

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection="row"
      justifyContent="space-between"
      flexWrap="wrap"
      minWidth="100%"
      rowGap={theme.spacing(theme.tokens.spacing.md)}
      columnGap={theme.spacing(theme.tokens.spacing.md)}
    >
      {logo && (
        <Box
          alt={logoAlt || ""}
          component="img"
          height={theme.spacing(theme.tokens.spacing.lg)}
          width="auto"
          src={`https:${logo.fields.file.url}`}
        />
      )}
      {offerCopy && <Chip label={offerCopy} sx={{ backgroundColor: theme.palette.success.light }} />}
    </Box>
  )
}

function Headline({ text }: { text: string }): JSX.Element {
  return (
    <Typography variant="h6" component="h5">
      {text}
    </Typography>
  )
}

function CtaButton({
  url,
  text,
  method,
  trackClick
}: {
  url: string
  text: string
  method?: string
  trackClick: (action: MemberActions) => void
}): JSX.Element {
  if (method === "dynamic") {
    return <DynamicCtaButton actionPath={url} text={text} trackClick={trackClick} />
  }

  return (
    <Button
      href={url}
      fullWidth={false}
      color="secondary"
      size="medium"
      variant="outlined"
      target="_blank"
      onClick={() => trackClick(MemberActions.CARROT_COLLECTIVE_CLICKED_CLAIM_DISCOUNT)}
    >
      {text}
    </Button>
  )
}

function LearnMoreToggle({ isExpanded, onClick }: { isExpanded: boolean; onClick: () => void }): JSX.Element {
  return (
    <Link
      component="button"
      endIcon={isExpanded ? <UpIcon fontSize="small" color="primary" /> : <DownIcon fontSize="small" color="primary" />}
      onClick={onClick}
    >
      <FormattedMessage defaultMessage="Learn more" />
    </Link>
  )
}

function TileContentContainer({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Stack
      spacing={theme.spacing(theme.tokens.spacing.md)}
      padding={theme.spacing(theme.tokens.spacing.md)}
      alignItems="start"
      justifyContent="space-between"
      sx={{ flexGrow: 1 }}
    >
      {children}
    </Stack>
  )
}

function UpperContentGroup({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.md)} alignItems="start">
      {children}
    </Stack>
  )
}

function LowerActionGroup({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "row", md: "column" },
        alignItems: { xs: "center", md: "flex-start" },
        justifyContent: { xs: "space-between", md: "flex-start" },
        gap: { xs: 0, md: theme.spacing(theme.tokens.spacing.md) },
        inlineSize: "100%",
        marginBlockEnd: theme.spacing(theme.tokens.spacing.md),
        marginBlockStart: theme.spacing(theme.tokens.spacing.md)
      }}
    >
      {children}
    </Box>
  )
}

function LearnAboutCoverageLink({ trackClick }: { trackClick: (action: MemberActions) => void }): JSX.Element {
  return (
    <ReactRouterLink
      to={DbgPaths.CARROT_ELIGIBLE}
      data-testid="learn-about-coverage-link"
      onClick={() => trackClick(MemberActions.CARROT_COLLECTIVE_CLICKED_LEARN_MORE_ABOUT_COVERAGE)}
    >
      <Link component="span">
        <FormattedMessage defaultMessage="Learn about coverage" />
      </Link>
    </ReactRouterLink>
  )
}

function ContentBlock({
  content
}: {
  content?: Entry<TypeContentBlockSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>[]
}): JSX.Element | null {
  if (!content || content.length === 0) return null
  return (
    <Box>
      <ContentfulContentRenderer contents={content} />
    </Box>
  )
}
